import { RecordingPermissions, UIConfig } from '../types/whiteboardProps';

/**
 * Merges deprecated recording props with new recordingPermissions system
 * Prioritizes recordingPermissions if both are provided
 */
export function getEffectiveRecordingPermissions(
  recordingPermissions?: RecordingPermissions,
  deprecatedProps?: {
    showRecordingControls?: boolean;
    allowRecording?: boolean;
    showRecordingsList?: boolean;
    maxRecordingDuration?: number;
    recordingQuality?: 'low' | 'medium' | 'high';
    autoSaveRecording?: boolean;
  }
): RecordingPermissions {
  // If recordingPermissions is provided, use it directly
  if (recordingPermissions) {
    return recordingPermissions;
  }

  // Otherwise, build from deprecated props
  const permissions: RecordingPermissions = {
    canRecord: deprecatedProps?.allowRecording ?? true,
    canViewRecordings: deprecatedProps?.showRecordingsList ?? true,
    recordingVisibility: {
      showOwnRecordings: true,
      showTenantRecordings: true,
      showPublicRecordings: true,
    },
    recordingManagement: {
      canDeleteOwnRecordings: true,
      canExportOwnRecordings: false,
      canShareRecordings: true,
      canMakeRecordingsPublic: false,
    },
    recordingSettings: {
      maxDuration: deprecatedProps?.maxRecordingDuration,
      quality: deprecatedProps?.recordingQuality ?? 'medium',
      autoSave: deprecatedProps?.autoSaveRecording ?? false,
      autoName: true,
    },
  };

  // If showRecordingControls is false, disable recording
  if (deprecatedProps?.showRecordingControls === false) {
    permissions.canRecord = false;
  }

  return permissions;
}

/**
 * Gets effective permissions from UIConfig (for backward compatibility)
 */
export function getEffectiveRecordingPermissionsFromConfig(uiConfig?: UIConfig): RecordingPermissions {
  return getEffectiveRecordingPermissions(
    uiConfig?.recordingPermissions,
    {
      showRecordingControls: uiConfig?.showRecordingControls,
      allowRecording: uiConfig?.allowRecording,
      showRecordingsList: uiConfig?.showRecordingsList,
      maxRecordingDuration: uiConfig?.maxRecordingDuration,
      recordingQuality: uiConfig?.recordingQuality,
      autoSaveRecording: uiConfig?.autoSaveRecording,
    }
  );
}

/**
 * Checks if a specific recording permission is enabled
 */
export function hasRecordingPermission(
  permission: keyof RecordingPermissions,
  uiConfig?: UIConfig
): boolean {
  const permissions = getEffectiveRecordingPermissionsFromConfig(uiConfig);
  const value = permissions[permission];
  return typeof value === 'boolean' ? value : true;
}

/**
 * Checks if a specific visibility permission is enabled
 */
export function hasRecordingVisibilityPermission(
  visibility: keyof NonNullable<RecordingPermissions['recordingVisibility']>,
  uiConfig?: UIConfig
): boolean {
  const permissions = getEffectiveRecordingPermissionsFromConfig(uiConfig);
  return permissions.recordingVisibility?.[visibility] ?? true;
}

/**
 * Checks if a specific management permission is enabled
 */
export function hasRecordingManagementPermission(
  management: keyof NonNullable<RecordingPermissions['recordingManagement']>,
  uiConfig?: UIConfig
): boolean {
  const permissions = getEffectiveRecordingPermissionsFromConfig(uiConfig);
  return permissions.recordingManagement?.[management] ?? 
    (management === 'canDeleteOwnRecordings' || management === 'canShareRecordings');
}

/**
 * Gets recording settings with defaults
 */
export function getRecordingSettings(uiConfig?: UIConfig): NonNullable<RecordingPermissions['recordingSettings']> {
  const permissions = getEffectiveRecordingPermissionsFromConfig(uiConfig);
  return {
    maxDuration: permissions.recordingSettings?.maxDuration,
    quality: permissions.recordingSettings?.quality ?? 'medium',
    autoSave: permissions.recordingSettings?.autoSave ?? false,
    autoName: permissions.recordingSettings?.autoName ?? true,
  };
}